import { IconShield } from '@developerz/ui';
import { createEffect, createMemo, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { type MintInput, tokenMintCall } from '../lib/tokens';
import { Field } from './Field';
import { MintScopePicker } from './MintScopePicker';
import { MintTokenDisclosure } from './MintTokenDisclosure';
import { Modal } from './Modal';
import { Button, buttonClass } from './ui/Button';

// Mint-a-PAT modal (#835). Presenter — the route owns the POST and reveals the
// plaintext token once it lands.
//
// The scope list is the SERVER's (`mintable_scopes`, `GET /v1/tokens`), never a
// hardcoded catalog: the exact vocabulary the mint would accept from this caller,
// so the picker can neither offer a scope that 403s nor drift behind a new one.
// `<MintScopePicker/>` renders it and states why it groups.
//
// THE TOKEN IS NAMED AT THE MINT, required here though the API takes it as
// optional (#2828) — only the two `pat` doors have a human to ask, and this is
// one of them. Unnamed, a token is unidentifiable afterwards: every PAT stores
// the same `dev_pat_` prefix and the secret exists for one render.
//
// The mint DISCLOSES ITS CALL, like every other mutating control (`<CopyAsCall/>`,
// `apps/dashboard/CLAUDE.md`) — and this one earns it twice over, because minting
// a PAT from CI is the very thing the copied command needs a PAT for. It is built
// from `chosen()`, the SAME value `submit` sends, so the snippet cannot describe a
// different request than the button fires.

export type MintTokenModalProps = {
  readonly open: boolean;
  /** The scopes this caller may mint (server-derived vocabulary). */
  readonly mintable: readonly string[];
  /** True while the POST is in flight — disables the submit. */
  readonly pending: boolean;
  /** Human error from the API, shown inline. */
  readonly error?: string | null;
  readonly onMint: (input: MintInput) => void;
  readonly onClose: () => void;
};

export function MintTokenModal(props: MintTokenModalProps): JSX.Element {
  const [name, setName] = createSignal('');
  const [selected, setSelected] = createSignal<ReadonlySet<string>>(new Set());

  // Default = everything on offer (the route's own default), re-armed on each
  // open so a dismissed half-made selection can't greet the next mint — and the
  // name with it, since a label left over from a dismissed mint would ride onto
  // the next token and misname it for as long as it lives.
  createEffect(() => {
    if (props.open) {
      setName('');
      setSelected(new Set(props.mintable));
    }
  });

  const toggle = (scope: string): void => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  };

  // The scopes the mint would carry, ordered by the server's vocabulary rather
  // than by click order — the token's scopes then read the way the picker did.
  // ONE memo, because `submit` and the snippet must not be two answers to
  // "which scopes": the copied command is only an equivalent while it is built
  // from the value the button sends.
  const chosen = createMemo(() => props.mintable.filter((s) => selected().has(s)));

  // The trimmed label, because that is what the API stores (`ApiTokenNameSchema`
  // trims) — so the snippet and the submit describe the same string, and a name
  // of only spaces is refused here rather than at the edge.
  const label = createMemo(() => name().trim());
  const valid = (): boolean => label().length > 0 && selected().size > 0;

  const submit = (e: Event): void => {
    e.preventDefault();
    if (!valid() || props.pending) return;
    props.onMint({ name: label(), scopes: chosen() });
  };

  return (
    <Modal
      open={props.open}
      size="lg"
      title={t('account_tokens.mint_title')}
      desc={t('account_tokens.mint_desc')}
      onClose={props.onClose}
    >
      <form class="mint-token__form" onSubmit={submit}>
        <div class="alert alert--icon" role="alert">
          <IconShield />
          <p class="alert__title">{t('account_tokens.reveal_once_title')}</p>
          <p class="alert__desc">{t('account_tokens.reveal_once_desc')}</p>
        </div>

        <Field
          label={t('account_tokens.form_name')}
          id="mint-token-name"
          desc={t('account_tokens.form_name_desc')}
        >
          {(a11y) => (
            <input
              {...a11y}
              class="input"
              type="text"
              maxLength={60}
              value={name()}
              onInput={(e) => setName(e.currentTarget.value)}
              placeholder={t('account_tokens.form_name_placeholder')}
              required
            />
          )}
        </Field>

        <MintScopePicker
          mintable={props.mintable}
          selected={selected()}
          onSelect={(scopes) => setSelected(new Set(scopes))}
          onToggle={toggle}
        />

        {props.error ? (
          <p class="field__error" role="alert">
            {props.error}
          </p>
        ) : null}

        {/* Withheld until the draft would pass, so the snippet is always the
            request the button would fire. An empty selection could only 400, and
            an empty `scopes` LIST is not the absence `tokenMintCall` reads as
            "everything this caller may mint" — printing `{"scopes":[]}` would
            teach the opposite of what omitting the field does. The name is in the
            same gate, and so is a caller whose `mintable` is empty. */}
        <Show when={valid()}>
          <MintTokenDisclosure call={tokenMintCall({ name: label(), scopes: chosen() })} />
        </Show>

        <div class="dialog__footer">
          <Button variant="outline" onClick={props.onClose}>
            {t('account_tokens.cancel')}
          </Button>
          <button type="submit" class={buttonClass()} disabled={!valid() || props.pending}>
            {t(props.pending ? 'account_tokens.minting' : 'account_tokens.mint')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
